import { useState, useEffect, useRef, useMemo } from "react";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  CalendarCheck, MagnifyingGlass, Funnel, User, MapPin, 
  Calendar, ChatText, LockSimple, Plus, Trash, NotePencil, 
  Link as LinkIcon, Factory, Buildings, HardHat, PencilRuler,
  ListBullets, CalendarBlank, CaretLeft, CaretRight, Clock
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/PageHeader";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useSettings } from "@/contexts/SettingsContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icon mapping for entity types
const getEntityIcon = (entityType, gabineteType) => {
  if (entityType === "client") return User;
  if (entityType === "gabinete") {
    if (gabineteType === "arquiteto") return PencilRuler;
    if (gabineteType === "construtor") return HardHat;
    if (gabineteType === "designer") return Buildings;
    return Buildings;
  }
  return Factory;
};

const getEntityColor = (entityType) => {
  if (entityType === "client") return "bg-[#009392]/10 text-[#009392]";
  if (entityType === "gabinete") return "bg-purple-100 text-purple-600";
  return "bg-amber-100 text-amber-600";
};

export default function Visitas({ hideHeader = false }) {
  const navigate = useNavigate();
  const { isReadOnly } = useSettings();
  const isLoggedIn = !isReadOnly;
  
  const [visits, setVisits] = useState([]);
  const [allVisits, setAllVisits] = useState([]); // unfiltered, used for the "Última Visita por Cliente" ranking (independent of list/calendar filters)
  const [entities, setEntities] = useState([]);
  const [inactiveClientIds, setInactiveClientIds] = useState(new Set()); // clientes com visit_frequency_days=0 ("Inativo") em Gestão de Alertas - excluídos da "Última Visita por Cliente"
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("lista"); // 'lista' | 'calendario'
  const [filters, setFilters] = useState({
    entity_id: "",
    entity_type: "",
    month: "",
    date: "",
    search: ""
  });

  // Visit form state
  const [showVisitDialog, setShowVisitDialog] = useState(false);
  const [editingVisit, setEditingVisit] = useState(null);
  const [visitForm, setVisitForm] = useState({
    client_id: "",
    gabinete_id: "",
    custom_name: "",
    visit_date: new Date().toISOString().split('T')[0],
    comments: ""
  });
  const [savingVisit, setSavingVisit] = useState(false);
  const [entitySearch, setEntitySearch] = useState("");
  const [showEntityDropdown, setShowEntityDropdown] = useState(false);
  const entityInputRef = useRef(null);

  useEffect(() => {
    if (!isLoggedIn) {
      setLoading(false);
      return;
    }
    fetchData();
  }, [isLoggedIn]);

  const fetchData = async () => {
    try {
      const [visitsRes, entitiesRes, alertSettingsRes] = await Promise.all([
        axios.get(`${API}/visits`),
        axios.get(`${API}/visits/entities`),
        axios.get(`${API}/alert-settings/clients`)
      ]);
      setVisits(visitsRes.data);
      setAllVisits(visitsRes.data);
      setEntities(entitiesRes.data);
      setInactiveClientIds(new Set(
        (alertSettingsRes.data || [])
          .filter(s => s.visit_frequency_days === 0)
          .map(s => s.client_id)
      ));
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const fetchFilteredVisits = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.entity_id && filters.entity_type) {
        params.append("entity_id", filters.entity_id);
        params.append("entity_type", filters.entity_type);
      }
      if (filters.date) params.append("date", filters.date);
      else if (filters.month) params.append("month", filters.month);
      if (filters.search) params.append("search", filters.search);
      
      const response = await axios.get(`${API}/visits?${params.toString()}`);
      setVisits(response.data);
    } catch (error) {
      console.error("Error fetching visits:", error);
      toast.error("Erro ao filtrar visitas");
    }
  };

  useEffect(() => {
    if (!loading && isLoggedIn) {
      fetchFilteredVisits();
    }
  }, [filters, loading, isLoggedIn]);

  const clearFilters = () => {
    setFilters({ entity_id: "", entity_type: "", month: "", date: "", search: "" });
  };

  const getMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('pt-PT', { month: 'long', year: 'numeric' });
      options.push({ value, label: label.charAt(0).toUpperCase() + label.slice(1) });
    }
    return options;
  };

  // ---- Calendar view helpers ----
  const todayISO = new Date().toISOString().split('T')[0];

  const getDisplayedMonth = () => {
    if (filters.date) return filters.date.slice(0, 7);
    if (filters.month) return filters.month;
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  };

  const navigateMonth = (delta) => {
    const [y, m] = getDisplayedMonth().split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setFilters(prev => ({ ...prev, month: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, date: "" }));
  };

  const goToCurrentMonth = () => {
    const now = new Date();
    setFilters(prev => ({ ...prev, month: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`, date: "" }));
  };

  const buildCalendarGrid = () => {
    const [y, m] = getDisplayedMonth().split('-').map(Number);
    const firstDay = new Date(y, m - 1, 1);
    const daysInMonth = new Date(y, m, 0).getDate();
    let startWeekday = firstDay.getDay(); // 0 = Sunday
    startWeekday = startWeekday === 0 ? 6 : startWeekday - 1; // Monday = 0
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      cells.push({ date: dateStr, dayNum: d, visits: visits.filter(v => v.visit_date === dateStr) });
    }
    return cells;
  };

  const displayedMonthLabel = (() => {
    const [y, m] = getDisplayedMonth().split('-').map(Number);
    const label = new Date(y, m - 1, 1).toLocaleDateString('pt-PT', { month: 'long', year: 'numeric' });
    return label.charAt(0).toUpperCase() + label.slice(1);
  })();

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-PT', { 
      day: '2-digit', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  // Última Visita por Cliente - ranking de TODOS os clientes (não afetado pelos filtros da
  // lista/calendário à direita, por isso usa `allVisits`), do mais tempo sem visita ao mais
  // recente; clientes nunca visitados aparecem sempre no topo.
  const clientVisitRanking = useMemo(() => {
    const lastVisitByClient = {};
    allVisits.forEach(v => {
      if (v.entity_type === 'client' && v.client_id && v.visit_date) {
        if (!lastVisitByClient[v.client_id] || v.visit_date > lastVisitByClient[v.client_id]) {
          lastVisitByClient[v.client_id] = v.visit_date;
        }
      }
    });
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return entities
      .filter(e => e.type === 'client' && !inactiveClientIds.has(e.id))
      .map(c => {
        const lastVisitDate = lastVisitByClient[c.id] || null;
        let daysSince = null;
        if (lastVisitDate) {
          const d = new Date(lastVisitDate);
          d.setHours(0, 0, 0, 0);
          daysSince = Math.max(0, Math.round((today - d) / (1000 * 60 * 60 * 24)));
        }
        return { ...c, lastVisitDate, daysSince };
      })
      .sort((a, b) => {
        if (a.daysSince === null && b.daysSince === null) return a.name.localeCompare(b.name);
        if (a.daysSince === null) return -1;
        if (b.daysSince === null) return 1;
        return b.daysSince - a.daysSince;
      });
  }, [entities, allVisits, inactiveClientIds]);

  // Filter entities based on search
  const filteredEntities = entities.filter(e => 
    e.name.toLowerCase().includes(entitySearch.toLowerCase()) ||
    e.label.toLowerCase().includes(entitySearch.toLowerCase())
  );

  // Select an entity from dropdown
  const selectEntity = (entity) => {
    if (entity.type === "client") {
      setVisitForm(prev => ({
        ...prev,
        client_id: entity.id,
        gabinete_id: "",
        custom_name: ""
      }));
    } else {
      setVisitForm(prev => ({
        ...prev,
        client_id: "",
        gabinete_id: entity.id,
        custom_name: ""
      }));
    }
    setEntitySearch(entity.name);
    setShowEntityDropdown(false);
  };

  // Handle custom name input
  const handleEntitySearchChange = (value) => {
    setEntitySearch(value);
    setShowEntityDropdown(true);
    
    const matchingEntity = entities.find(e => e.name.toLowerCase() === value.toLowerCase());
    if (matchingEntity) {
      selectEntity(matchingEntity);
    } else {
      setVisitForm(prev => ({
        ...prev,
        client_id: "",
        gabinete_id: "",
        custom_name: value
      }));
    }
  };

  // Save visit
  const saveVisit = async () => {
    if (!visitForm.visit_date) {
      toast.error("Selecione uma data");
      return;
    }
    
    if (!visitForm.client_id && !visitForm.gabinete_id && !visitForm.custom_name.trim()) {
      toast.error("Selecione uma entidade ou escreva um nome");
      return;
    }
    
    setSavingVisit(true);
    try {
      const payload = {
        client_id: visitForm.client_id || null,
        gabinete_id: visitForm.gabinete_id || null,
        custom_name: (!visitForm.client_id && !visitForm.gabinete_id) ? visitForm.custom_name.trim() : null,
        visit_date: visitForm.visit_date,
        comments: visitForm.comments
      };
      
      if (editingVisit) {
        await axios.put(`${API}/visits/${editingVisit.id}`, payload);
        toast.success("Visita atualizada");
      } else {
        await axios.post(`${API}/visits`, payload);
        toast.success("Visita registada");
      }
      
      closeDialog();
      fetchData();
    } catch (error) {
      console.error("Error saving visit:", error);
      toast.error("Erro ao guardar visita");
    } finally {
      setSavingVisit(false);
    }
  };

  // Delete visit
  const deleteVisit = async (visitId) => {
    if (!window.confirm("Tem a certeza que deseja eliminar esta visita?")) return;
    try {
      await axios.delete(`${API}/visits/${visitId}`);
      toast.success("Visita eliminada");
      fetchData();
    } catch (error) {
      toast.error("Erro ao eliminar visita");
    }
  };

  // Open edit dialog
  const openEditVisit = (visit) => {
    setEditingVisit(visit);
    setVisitForm({
      client_id: visit.client_id || "",
      gabinete_id: visit.gabinete_id || "",
      custom_name: visit.custom_name || "",
      visit_date: visit.visit_date,
      comments: visit.comments || ""
    });
    setEntitySearch(visit.entity_name || visit.custom_name || "");
    setShowVisitDialog(true);
  };

  // Open new visit dialog
  const openNewVisit = () => {
    setEditingVisit(null);
    setVisitForm({
      client_id: "",
      gabinete_id: "",
      custom_name: "",
      visit_date: new Date().toISOString().split('T')[0],
      comments: ""
    });
    setEntitySearch("");
    setShowVisitDialog(true);
  };

  // Close dialog
  const closeDialog = () => {
    setShowVisitDialog(false);
    setEditingVisit(null);
    setEntitySearch("");
    setShowEntityDropdown(false);
  };

  // Navigate to entity detail
  const navigateToEntity = (visit) => {
    if (visit.entity_type === "client" && visit.client_id) {
      navigate(`/clients/${visit.client_id}`);
    } else if (visit.entity_type === "gabinete" && visit.gabinete_id) {
      navigate(`/promocao/${visit.gabinete_id}`);
    }
  };

  // Handle entity filter change
  const handleEntityFilterChange = (value) => {
    if (value === "all") {
      setFilters(prev => ({ ...prev, entity_id: "", entity_type: "" }));
    } else {
      const [type, id] = value.split("|");
      setFilters(prev => ({ ...prev, entity_id: id, entity_type: type }));
    }
  };

  if (!isLoggedIn) {
    return (
      <div className={hideHeader ? "flex items-center justify-center py-16" : "min-h-screen bg-stone-50 flex items-center justify-center"}>
        <div className="text-center p-8">
          <div className="p-4 bg-stone-200 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
            <LockSimple size={40} className="text-stone-500" />
          </div>
          <h2 className="text-xl font-bold text-stone-900 mb-2">Acesso Restrito</h2>
          <p className="text-stone-500 mb-4">Inicie sessão para aceder ao registo de visitas</p>
          {!hideHeader && (
            <Button onClick={() => navigate('/')} className="btn-primary">
              Voltar ao Início
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={hideHeader ? "" : "min-h-screen bg-stone-50"}>
      {/* Header */}
      {!hideHeader && (
      <div className="bg-white border-b border-stone-200 px-4 py-4">
        <div className="max-w-7xl mx-auto">
          <PageHeader
            icon={CalendarCheck}
            title="Registo de Visitas"
            subtitle={`${visits.length} visitas registadas`}
            actions={
              <Button className="btn-primary" onClick={openNewVisit} data-testid="btn-new-visit">
                <Plus size={18} />
                Nova Visita
              </Button>
            }
          />
        </div>
      </div>
      )}
      {hideHeader && (
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-stone-500">{visits.length} visitas registadas</p>
          <Button className="btn-primary" onClick={openNewVisit} data-testid="btn-new-visit">
            <Plus size={18} />
            Nova Visita
          </Button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border-b border-stone-200 px-4 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 text-stone-500">
              <Funnel size={18} />
              <span className="text-sm font-medium">Filtros:</span>
            </div>
            
            <Select
              value={filters.entity_id ? `${filters.entity_type}|${filters.entity_id}` : "all"}
              onValueChange={handleEntityFilterChange}
            >
              <SelectTrigger className="w-[250px]">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos (Clientes + Promoção)</SelectItem>
                <SelectItem value="clients-header" disabled className="font-semibold text-[#009392]">
                  ── Clientes ──
                </SelectItem>
                {entities.filter(e => e.type === "client").map(entity => (
                  <SelectItem key={entity.id} value={`client|${entity.id}`}>
                    {entity.name}
                  </SelectItem>
                ))}
                <SelectItem value="gabinetes-header" disabled className="font-semibold text-purple-600">
                  ── Promoção ──
                </SelectItem>
                {entities.filter(e => e.type === "gabinete").map(entity => (
                  <SelectItem key={entity.id} value={`gabinete|${entity.id}`}>
                    {entity.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filters.month || "all"}
              onValueChange={(value) => setFilters(prev => ({ ...prev, month: value === "all" ? "" : value, date: "" }))}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Todos os meses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os meses</SelectItem>
                {getMonthOptions().map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Input
              type="date"
              value={filters.date}
              onChange={(e) => setFilters(prev => ({ ...prev, date: e.target.value, month: "" }))}
              className="w-[160px]"
              data-testid="input-visit-filter-date"
              title="Filtrar por dia específico"
            />

            <div className="relative flex-1 min-w-[200px] max-w-[300px]">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <Input
                placeholder="Pesquisar..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="pl-10"
              />
            </div>

            {(filters.entity_id || filters.month || filters.date || filters.search) && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Limpar filtros
              </Button>
            )}

            <div className="flex items-center gap-0.5 bg-stone-100 rounded-lg p-1 ml-auto" data-testid="visits-view-toggle">
              <button
                onClick={() => setViewMode("lista")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === "lista" ? "bg-white text-[#009392] shadow-sm" : "text-stone-500 hover:text-stone-700"}`}
                data-testid="btn-view-lista"
              >
                <ListBullets size={16} /> Lista
              </button>
              <button
                onClick={() => setViewMode("calendario")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === "calendario" ? "bg-white text-[#009392] shadow-sm" : "text-stone-500 hover:text-stone-700"}`}
                data-testid="btn-view-calendario"
              >
                <CalendarBlank size={16} /> Calendário
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6 flex flex-col lg:flex-row gap-6">
        {/* LEFT: conteúdo atual (calendário/lista de visitas) */}
        <div className="flex-1 min-w-0">
        {viewMode === "calendario" && !loading && (
          <div className="flex items-center justify-center gap-3 mb-4">
            <button onClick={() => navigateMonth(-1)} className="p-2 hover:bg-stone-100 rounded-lg" data-testid="btn-prev-month">
              <CaretLeft size={18} />
            </button>
            <span className="text-sm font-semibold text-stone-700 min-w-[160px] text-center" data-testid="calendar-month-label">
              {displayedMonthLabel}
            </span>
            <button onClick={() => navigateMonth(1)} className="p-2 hover:bg-stone-100 rounded-lg" data-testid="btn-next-month">
              <CaretRight size={18} />
            </button>
            <Button variant="outline" size="sm" onClick={goToCurrentMonth} data-testid="btn-today-month">
              Hoje
            </Button>
          </div>
        )}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner size={40} />
          </div>
        ) : visits.length === 0 ? (
          <div className="text-center py-12">
            <CalendarCheck size={48} className="mx-auto text-stone-300 mb-4" />
            <h3 className="text-lg font-medium text-stone-900 mb-2">Sem visitas registadas</h3>
            <p className="text-stone-500 mb-4">Clique em &quot;Nova Visita&quot; para registar uma visita</p>
          </div>
        ) : viewMode === "calendario" ? (
          <div className="bg-white rounded-lg border border-stone-200 overflow-hidden" data-testid="visits-calendar-grid">
            <div className="grid grid-cols-7 bg-stone-50 border-b border-stone-200">
              {["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"].map(d => (
                <div key={d} className="text-center text-xs font-semibold text-stone-500 py-2">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {buildCalendarGrid().map((cell, idx) => (
                <div
                  key={idx}
                  className={`border-b border-r border-stone-100 min-h-[110px] p-1.5 ${cell && cell.date === todayISO ? 'bg-[#009392]/5' : ''}`}
                  data-testid={cell ? `calendar-day-${cell.date}` : undefined}
                >
                  {cell && (
                    <>
                      <p className={`text-xs font-semibold mb-1 ${cell.date === todayISO ? 'text-[#009392]' : 'text-stone-500'}`}>{cell.dayNum}</p>
                      <div className="space-y-1 max-h-[100px] overflow-y-auto">
                        {cell.visits.map(visit => {
                          const IconComponent = getEntityIcon(visit.entity_type, visit.gabinete_type);
                          return (
                            <div
                              key={visit.id}
                              onClick={() => openEditVisit(visit)}
                              className="flex items-center gap-1 px-1.5 py-1 rounded bg-stone-50 hover:bg-[#009392]/10 cursor-pointer group"
                              data-testid={`calendar-visit-${visit.id}`}
                              title={visit.entity_name}
                            >
                              <IconComponent size={11} className="text-stone-400 flex-shrink-0" />
                              <span className="text-[11px] text-stone-700 truncate flex-1">{visit.entity_name}</span>
                              <button
                                onClick={(e) => { e.stopPropagation(); deleteVisit(visit.id); }}
                                className="opacity-0 group-hover:opacity-100 text-stone-400 hover:text-red-500 flex-shrink-0"
                                title="Eliminar"
                              >
                                <Trash size={11} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {visits.map((visit) => {
              const IconComponent = getEntityIcon(visit.entity_type, visit.gabinete_type);
              const colorClass = getEntityColor(visit.entity_type);
              
              return (
                <div 
                  key={visit.id}
                  className="bg-white rounded-lg border border-stone-200 p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg flex-shrink-0 ${colorClass}`}>
                        <IconComponent size={20} weight="duotone" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 
                            className={`font-semibold ${
                              visit.is_linked 
                                ? 'text-stone-900 hover:text-[#009392] cursor-pointer' 
                                : 'text-amber-700'
                            }`}
                            onClick={() => visit.is_linked && navigateToEntity(visit)}
                          >
                            {visit.entity_name}
                          </h3>
                          {visit.is_linked && (
                            <LinkIcon size={14} className="text-stone-400" title="Vinculado" />
                          )}
                          {visit.entity_type === "gabinete" && visit.gabinete_type && (
                            <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-600 rounded-full">
                              {visit.gabinete_type === "arquiteto" ? "Arquitecto" : 
                               visit.gabinete_type === "construtor" ? "Construtor" : 
                               visit.gabinete_type === "designer" ? "Designer" : visit.gabinete_type}
                            </span>
                          )}
                        </div>
                        {visit.entity_city && (
                          <div className="flex items-center gap-1 text-sm text-stone-500">
                            <MapPin size={14} />
                            <span>{visit.entity_city}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2 text-sm text-stone-600">
                        <Calendar size={16} />
                        <span className="font-medium">{formatDate(visit.visit_date)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => openEditVisit(visit)}
                          className="p-1.5 text-stone-400 hover:text-[#009392] hover:bg-stone-100 rounded transition-colors"
                          title="Editar"
                        >
                          <NotePencil size={18} />
                        </button>
                        <button
                          onClick={() => deleteVisit(visit.id)}
                          className="p-1.5 text-stone-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                          title="Eliminar"
                        >
                          <Trash size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  {visit.comments && (
                    <div className="mt-3 pt-3 border-t border-stone-100">
                      <div className="flex items-start gap-2">
                        <ChatText size={16} className="text-stone-400 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-stone-600">{visit.comments}</p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        </div>

        {/* RIGHT: Última Visita por Cliente - ajuda a priorizar visitas. */}
        <div className="lg:w-72 flex-shrink-0 space-y-6">
        <div data-testid="ultima-visita-cliente-panel">
          <div className="bg-white rounded-lg border border-stone-200 p-4 lg:sticky lg:top-4">
            <h3 className="font-semibold text-stone-800 mb-1 flex items-center gap-2">
              <Clock size={18} className="text-[#009392]" />
              Última Visita por Cliente
            </h3>
            <p className="text-xs text-stone-400 mb-3">Do mais tempo sem visita ao mais recente.</p>
            {clientVisitRanking.length === 0 ? (
              <p className="text-sm text-stone-400 text-center py-4">Sem clientes registados.</p>
            ) : (
              <div className="space-y-1 max-h-[70vh] overflow-y-auto">
                {clientVisitRanking.map(c => (
                  <button
                    key={c.id}
                    onClick={() => navigate(`/clients/${c.id}`)}
                    className="w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg hover:bg-stone-50 text-left transition-colors"
                    data-testid={`ultima-visita-row-${c.id}`}
                  >
                    <span className="text-sm text-stone-700 truncate">{c.name}</span>
                    <span
                      className={`text-xs font-semibold flex-shrink-0 ${
                        c.daysSince === null || c.daysSince > 60 ? 'text-red-500' :
                        c.daysSince > 30 ? 'text-amber-500' : 'text-stone-400'
                      }`}
                      data-testid={`ultima-visita-dias-${c.id}`}
                    >
                      {c.daysSince === null ? 'Nunca' : c.daysSince === 0 ? 'Hoje' : `${c.daysSince}d`}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        </div>
      </div>

      {/* Visit Dialog */}
      <Dialog open={showVisitDialog} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-[Manrope] flex items-center gap-2">
              <CalendarCheck size={20} className="text-[#00A19A]" />
              {editingVisit ? "Editar Visita" : "Registar Nova Visita"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Entity Selection */}
            <div>
              <Label>Cliente / Promoção / Local</Label>
              <p className="text-xs text-stone-500 mb-2">
                Selecione da lista ou escreva um nome personalizado (ex: FÁBRICA, OBRA)
              </p>
              <div className="relative">
                <Input
                  ref={entityInputRef}
                  value={entitySearch}
                  onChange={(e) => handleEntitySearchChange(e.target.value)}
                  onFocus={() => setShowEntityDropdown(true)}
                  placeholder="Escreva ou selecione..."
                  className="mt-1"
                  data-testid="input-entity-search"
                />
                
                {showEntityDropdown && entitySearch && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-stone-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredEntities.length > 0 ? (
                      <>
                        {/* Clients section */}
                        {filteredEntities.filter(e => e.type === "client").length > 0 && (
                          <>
                            <div className="px-3 py-1.5 text-xs font-semibold text-[#009392] bg-stone-50">
                              Clientes
                            </div>
                            {filteredEntities.filter(e => e.type === "client").slice(0, 5).map(entity => (
                              <button
                                key={entity.id}
                                onClick={() => selectEntity(entity)}
                                className="w-full px-3 py-2 text-left hover:bg-stone-50 flex items-center gap-2 text-sm"
                              >
                                <User size={16} className="text-[#009392]" />
                                <span>{entity.name}</span>
                                {entity.city && <span className="text-stone-400 text-xs">({entity.city})</span>}
                              </button>
                            ))}
                          </>
                        )}
                        
                        {/* Gabinetes section */}
                        {filteredEntities.filter(e => e.type === "gabinete").length > 0 && (
                          <>
                            <div className="px-3 py-1.5 text-xs font-semibold text-purple-600 bg-stone-50">
                              Promoção
                            </div>
                            {filteredEntities.filter(e => e.type === "gabinete").slice(0, 5).map(entity => {
                              const GabIcon = getEntityIcon("gabinete", entity.gabinete_type);
                              return (
                                <button
                                  key={entity.id}
                                  onClick={() => selectEntity(entity)}
                                  className="w-full px-3 py-2 text-left hover:bg-stone-50 flex items-center gap-2 text-sm"
                                >
                                  <GabIcon size={16} className="text-purple-600" />
                                  <span>{entity.label}</span>
                                </button>
                              );
                            })}
                          </>
                        )}
                      </>
                    ) : null}
                    
                    {/* Custom name option */}
                    {entitySearch && !entities.find(e => e.name.toLowerCase() === entitySearch.toLowerCase()) && (
                      <button
                        onClick={() => {
                          setVisitForm(prev => ({
                            ...prev,
                            client_id: "",
                            gabinete_id: "",
                            custom_name: entitySearch
                          }));
                          setShowEntityDropdown(false);
                        }}
                        className="w-full px-3 py-2 text-left hover:bg-amber-50 flex items-center gap-2 text-sm border-t border-stone-100 text-amber-700"
                      >
                        <Factory size={16} />
                        <span>Usar &quot;{entitySearch}&quot; (sem vincular)</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
              
              {/* Show selected status */}
              {visitForm.client_id && (
                <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                  <User size={12} />
                  Vinculado a Cliente
                </p>
              )}
              {visitForm.gabinete_id && (
                <p className="text-xs text-purple-600 mt-1 flex items-center gap-1">
                  <Buildings size={12} />
                  Vinculado a Promoção
                </p>
              )}
              {!visitForm.client_id && !visitForm.gabinete_id && visitForm.custom_name && (
                <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                  <Factory size={12} />
                  Nome personalizado (não vinculado)
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="visit-date">Data da Visita</Label>
              <Input
                id="visit-date"
                type="date"
                value={visitForm.visit_date}
                onChange={(e) => setVisitForm(prev => ({ ...prev, visit_date: e.target.value }))}
                className="mt-1.5"
                data-testid="input-visit-date"
              />
            </div>
            
            <div>
              <Label htmlFor="visit-comments">Comentários</Label>
              <Textarea
                id="visit-comments"
                value={visitForm.comments}
                onChange={(e) => setVisitForm(prev => ({ ...prev, comments: e.target.value }))}
                placeholder="Observações sobre a visita..."
                className="mt-1.5 min-h-[100px]"
                data-testid="input-visit-comments"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={closeDialog}>
              Cancelar
            </Button>
            <Button 
              className="btn-primary"
              onClick={saveVisit}
              disabled={savingVisit || (!visitForm.client_id && !visitForm.gabinete_id && !visitForm.custom_name.trim())}
            >
              {savingVisit ? "A guardar..." : (editingVisit ? "Atualizar" : "Registar")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
